import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { revertEmailChange } from '../api/profile';
import '../styles/pages/login.css';

/**
 * RevertEmailChangePage — lands from the "Undo this change" link mailed to the
 * OLD address after an email change completes (GS-2026-3402-4, Finding 4). The
 * revert is applied only on an explicit click, never on page load, so an email
 * link pre-fetcher (which issues a GET) can't trigger it. The signed token is
 * the credential; no login required.
 *
 *   /revert-email-change?token=…
 */
const RevertEmailChangePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleRevert = async () => {
    setError('');
    setLoading(true);
    try {
      await revertEmailChange(token);
      setDone(true);
    } catch (err) {
      setError(err.message || t('revertEmailChange.failed', 'This link is invalid or has expired.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__header">
            <div className="login-card__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </div>
            <h1 className="login-card__title">{t('revertEmailChange.invalidTitle', 'Invalid link')}</h1>
            <p className="login-card__subtitle">{t('revertEmailChange.invalidSubtitle', 'This undo link is invalid or has expired.')}</p>
          </div>
          <div className="login-card__form">
            <button type="button" className="login-card__submit" onClick={() => navigate('/login')}>
              {t('revertEmailChange.backToLogin', 'Back to Sign In')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__header">
            <div className="login-card__icon login-card__icon--success">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h1 className="login-card__title">{t('revertEmailChange.successTitle', 'Change undone')}</h1>
            <p className="login-card__subtitle">{t('revertEmailChange.successSubtitle', 'Your email has been restored. If you didn\'t start this, change your password too.')}</p>
          </div>
          <div className="login-card__form">
            <button type="button" className="login-card__submit" onClick={() => navigate('/login')}>
              {t('revertEmailChange.signIn', 'Sign In')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__header">
          <div className="login-card__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
          </div>
          <h1 className="login-card__title">{t('revertEmailChange.title', 'Undo email change')}</h1>
          <p className="login-card__subtitle">{t('revertEmailChange.subtitle', 'Restore your account to its previous email address.')}</p>
        </div>
        <div className="login-card__form">
          {error && <p className="form-error" role="alert" style={{ marginBottom: '12px' }}>{error}</p>}
          <button type="button" className="login-card__submit" onClick={handleRevert} disabled={loading}>
            {loading ? t('revertEmailChange.reverting', 'Undoing…') : t('revertEmailChange.confirm', 'Undo this change')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevertEmailChangePage;
